/**
 * Home-screen "Calendar" section. Defaults to Today's todo list; toggles to a
 * tap-a-day Week strip. Self-contained: owns its data hook (the real
 * api/resident-todos seam), so it slots into the mobile and desktop home views
 * unchanged.
 *
 * State model: `mode` + `selectedDate` only. The visible week is always
 * derived from selectedDate, and ‹ › paging just moves selectedDate ±7 days.
 */
import React, { useCallback, useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { GlassCard } from '../../GlassCard';
import { SectionHeader } from '../../SectionHeader';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { useTypography } from '@/hooks/shared/useTypography';
import { useSpacing } from '@/hooks/shared/useSpacing';
import { useResidentTodos } from '@/hooks/residents/useResidentTodos';
import {
  addDays,
  formatDayHeading,
  formatWeekRangeLabel,
  getWeekDays,
  groupTodosByDateKey,
  sortTodos,
  toDateKey,
} from '@/utils/residents/todoCalendar';
import type { ResidentTodo } from '@/types/residents/todos';
import { TodoCalendarTabs, type TodoCalendarMode } from './TodoCalendarTabs';
import { WeekStrip } from './WeekStrip';
import {
  TodoRow,
  TodoFormModal,
  type TodoFormValues,
} from '@/components/residents/redesign/todoCalendar';

interface TodoCalendarSectionProps {
  /** Test seam: pins "today". Defaults to the device clock at mount. */
  todayOverride?: Date;
  /** Opens the full Planner screen. When absent the header shows no action. */
  onViewAll?: () => void;
}

export const TodoCalendarSection: React.FC<TodoCalendarSectionProps> = ({
  todayOverride,
  onViewAll,
}) => {
  const [today] = useState<Date>(() => todayOverride ?? new Date());
  const {
    todos,
    toggleTodo,
    isLoading,
    isError,
    refetch,
    addTodo,
    updateTodo,
    deleteTodo,
  } = useResidentTodos();
  const [mode, setMode] = useState<TodoCalendarMode>('today');
  const [selectedDate, setSelectedDate] = useState<Date>(today);
  const [formVisible, setFormVisible] = useState(false);
  const [formTodo, setFormTodo] = useState<ResidentTodo | null>(null);

  const type = useTypography();
  const space = useSpacing();
  const styles = useThemedStyles(({ colors, semantic }) =>
    StyleSheet.create({
      card: {
        gap: space.lg,
      },
      headingRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        minHeight: 44,
      },
      heading: {
        ...type.cardTitleLarge,
        color: colors.text.primary,
        // Yield to "+ Add" on the row instead of pushing it off-screen.
        flexShrink: 1,
      },
      headingCount: {
        ...type.small,
        color: colors.text.muted,
      },
      addText: {
        ...type.button,
        color: semantic.interactive.primaryHover, // AA-safe blue ink
      },
      emptyWrap: {
        alignItems: 'center',
        paddingVertical: space['3xl'],
      },
      emptyText: {
        ...type.body,
        color: colors.text.muted,
      },
      retryText: {
        ...type.button,
        color: semantic.interactive.primaryHover, // AA-safe blue ink
        paddingTop: space.sm,
      },
    }),
  );

  const groups = useMemo(() => groupTodosByDateKey(todos), [todos]);
  const countsByKey = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const [key, list] of Object.entries(groups)) counts[key] = list.length;
    return counts;
  }, [groups]);

  const activeDate = mode === 'today' ? today : selectedDate;
  const activeKey = toDateKey(activeDate);
  const activeTodos = useMemo(
    () => sortTodos(groups[activeKey] ?? []),
    [groups, activeKey],
  );
  const dueCount = useMemo(
    () => activeTodos.filter(t => !t.completed).length,
    [activeTodos],
  );

  const weekDays = useMemo(() => getWeekDays(selectedDate), [selectedDate]);

  const handlePrevWeek = useCallback(() => setSelectedDate(d => addDays(d, -7)), []);
  const handleNextWeek = useCallback(() => setSelectedDate(d => addDays(d, 7)), []);

  const handleAdd = useCallback(() => {
    setFormTodo(null);
    setFormVisible(true);
  }, []);
  const handleEdit = useCallback((todo: ResidentTodo) => {
    setFormTodo(todo);
    setFormVisible(true);
  }, []);
  const handleCloseForm = useCallback(() => setFormVisible(false), []);
  // The modal owns try/catch; these just return the mutation promises. An edit
  // save always sends notes explicitly (null clears it server-side).
  const handleSaveForm = useCallback(
    (values: TodoFormValues) =>
      formTodo
        ? updateTodo({
            id: formTodo.id,
            title: values.title,
            dueDate: values.dueDate,
            notes: values.notes ?? null,
          })
        : addTodo(values),
    [formTodo, updateTodo, addTodo],
  );

  return (
    <View>
      <SectionHeader
        title="Calendar"
        actionLabel={onViewAll ? 'View all' : undefined}
        onAction={onViewAll}
      />
      <GlassCard style={styles.card}>
        <TodoCalendarTabs mode={mode} onModeChange={setMode} />
        {mode === 'week' && (
          <WeekStrip
            weekDays={weekDays}
            rangeLabel={formatWeekRangeLabel(weekDays)}
            selectedKey={toDateKey(selectedDate)}
            todayKey={toDateKey(today)}
            countsByKey={countsByKey}
            onSelectDay={setSelectedDate}
            onPrevWeek={handlePrevWeek}
            onNextWeek={handleNextWeek}
          />
        )}
        <View style={styles.headingRow}>
          <Text style={styles.heading}>
            {formatDayHeading(activeDate)}
            {dueCount > 0 ? (
              <Text style={styles.headingCount}>{` · ${dueCount} due`}</Text>
            ) : null}
          </Text>
          <TouchableOpacity
            onPress={handleAdd}
            // 14 per side reaches the 44pt target around ~17pt text (TodoRow's
            // pencil precedent).
            hitSlop={{ top: 14, bottom: 14, left: 14, right: 14 }}
            accessibilityRole="button"
            accessibilityLabel="Add todo"
          >
            <Text style={styles.addText}>+ Add</Text>
          </TouchableOpacity>
        </View>
        {isLoading ? (
          <View style={styles.emptyWrap}>
            <Text style={styles.emptyText}>Loading your todos…</Text>
          </View>
        ) : isError && todos.length === 0 ? (
          // Only a failure with NOTHING cached takes over the body. RTK Query
          // also raises `isError` when a background LIST refetch fails while
          // cached rows are still in hand — and a rejected mutation fires its
          // `invalidatesTags`, so a failed offline toggle (optimistic undo →
          // refetch rejects) would otherwise replace a populated day with the
          // error card. With data, we keep showing it and let the row's own
          // rollback be the feedback.
          <View style={styles.emptyWrap}>
            <Text style={styles.emptyText}>Couldn't load your todos.</Text>
            <TouchableOpacity
              onPress={refetch}
              accessibilityRole="button"
              accessibilityLabel="Retry loading todos"
              // 14 per side reaches the 44pt target around ~17pt text.
              hitSlop={{ top: 14, bottom: 14, left: 14, right: 14 }}
            >
              <Text style={styles.retryText}>Retry</Text>
            </TouchableOpacity>
          </View>
        ) : activeTodos.length === 0 ? (
          <View style={styles.emptyWrap}>
            <Text style={styles.emptyText}>
              {mode === 'today' ? 'Nothing due today' : 'Nothing due this day'}
            </Text>
          </View>
        ) : (
          <View>
            {activeTodos.map((todo, index) => (
              <TodoRow
                key={todo.id}
                todo={todo}
                isLast={index === activeTodos.length - 1}
                onToggle={toggleTodo}
                onEdit={handleEdit}
              />
            ))}
          </View>
        )}
      </GlassCard>
      <TodoFormModal
        visible={formVisible}
        todo={formTodo}
        initialDateKey={activeKey}
        onSave={handleSaveForm}
        onDelete={deleteTodo}
        onClose={handleCloseForm}
      />
    </View>
  );
};

TodoCalendarSection.displayName = 'TodoCalendarSection';
